import { BTText } from "@btnet/core-components/BTText";

import { SearchDialogRow } from "./SearchDialogRow";
import type { ISearchDialogRow } from "./searchDialogRow.types";

export const SEARCH_DIALOG_OPTION_ID_PREFIX = "search-dialog-option-";

/** What the combobox's `aria-controls` names. Only one list renders at a time. */
export const SEARCH_DIALOG_LISTBOX_ID = "search-dialog-listbox";

export interface ISearchDialogSection {
    /** Section heading, e.g. "Recent". Null renders the rows with no heading. */
    label: string | null;
    rows: ISearchDialogRow[];
}

export interface ISearchDialogListProps {
    sections: ISearchDialogSection[];
    /** Called with the flat index when the pointer moves over a row. */
    onHoverRow?: (index: number) => void;
    /**
     * Index into the flattened row order across every section, or -1 for none.
     * Headings are not navigable, so this counts rows only.
     */
    highlightedIndex: number;
}

/**
 * The dialog's navigable rows: one listbox, one flat highlight index, and a
 * `role="group"` per section.
 *
 * One listbox, not one per section, because `aria-activedescendant` must name an
 * option inside the element the input's `aria-controls` points at.
 */
export function SearchDialogList({
    sections,
    highlightedIndex,
    onHoverRow,
}: ISearchDialogListProps) {
    const visible = sections.filter((section) => section.rows.length > 0);

    // The highlight index spans sections, so each row needs to know how many
    // came before it. Computed up front rather than mutated during render.
    const offsets: number[] = [];
    visible.reduce((runningTotal, section) => {
        offsets.push(runningTotal);
        return runningTotal + section.rows.length;
    }, 0);

    return (
        <div
            role="listbox"
            id={SEARCH_DIALOG_LISTBOX_ID}
            aria-label="Search results"
            data-testid="search-dialog-list"
        >
            {visible.map((section, sectionIndex) => (
                <div
                    key={section.label ?? `section-${sectionIndex}`}
                    role="group"
                    aria-label={section.label ?? undefined}
                    className="flex flex-col gap-1 pt-1"
                    data-testid="search-dialog-section"
                >
                    {section.label !== null && (
                        <BTText
                            size="normal-md"
                            tag="div"
                            className="px-3 pb-1 pt-4 text-fg-tertiary max-md:px-2"
                            aria-hidden="true"
                        >
                            {section.label}
                        </BTText>
                    )}
                    {section.rows.map((row, rowIndex) => {
                        const index = offsets[sectionIndex] + rowIndex;
                        return (
                            <SearchDialogRow
                                key={row.key}
                                row={row}
                                role="option"
                                elementId={`${SEARCH_DIALOG_OPTION_ID_PREFIX}${index}`}
                                isHighlighted={index === highlightedIndex}
                                onHover={onHoverRow ? () => onHoverRow(index) : undefined}
                            />
                        );
                    })}
                </div>
            ))}
        </div>
    );
}
